<template>
	<div class="location-map rounded-3 bg-medium">
		<section
			id="map"
			ref="mapDiv"
			class="location-map-canvas rounded-3"
			label="Locations Map"
			style="overflow: hidden"
		/>

		<div v-if="showInfoWindow" v-show="selectedLocation" ref="infoWindowEl">
			<LocationInfoWindow :location="selectedLocation" @close-window="closeInfoWindow" />
		</div>
	</div>
</template>

<script setup lang="ts">
import { onMounted, ref, type Ref, watch } from 'vue';
import { useMapsLoader } from '@/composables/use-maps-loader';

import type { Location } from 'Types/aemui/find-a-location/findaLocation';
import LocationInfoWindow from 'Lib/components/find-a-location/LocationInfoWindow.vue';
import {
	mapConfig,
	defaultCenter,
	defaultZoom
} from 'Lib/components/find-a-location/util/mapConfig';
import {
	mapCenterIcon,
	smallLocationIcon,
	largeLocationIcon
} from 'Lib/components/find-a-location/util/mapIcons';
import { storeToRefs } from 'pinia';
import { useMapStore } from '@/stores/map';

const mapStore = useMapStore();
const { experienceType } = storeToRefs(mapStore);

interface LtLocationMarker extends google.maps.marker.AdvancedMarkerElement {
	mmsClubId: number; // we add this to the map markers so we know which location they represent
}

const props = defineProps<{
	mapCenter: google.maps.LatLngLiteral;
	allLocations: Location[];
	matchingLocations: Location[];
	hoveredLocation: Location | null;
	selectedLocation: Location | null;
}>();

const emit = defineEmits<{
	(e: 'marker-click', location: Location): void;
	(e: 'info-window-close'): void;
	(e: 'map-bounds-set', bounds: google.maps.LatLngBounds | undefined): void;
}>();

const mapDiv = ref<HTMLElement>();
const infoWindowEl = ref<HTMLElement>();

let map: google.maps.Map; // the google maps instance
const mapMarkers: LtLocationMarker[] = []; // all Life Time location markers currently displayed on the map
let centerMarker: google.maps.marker.AdvancedMarkerElement | null = null; // special marker for the center of the map (if user allows geolocation or has searched)

// The infoWindow that appears near a map marker when a location is selected.
// Since only one infoWindow can be open at a time, we create one infoWindow
// instance and set the location and contents as needed.
let infoWindow: google.maps.InfoWindow | null = null;

let CoreLibrary: google.maps.CoreLibrary;
let MapLibrary: google.maps.MapsLibrary;
let MarkerLibrary: google.maps.MarkerLibrary;
const zoom: Ref<number | null> = ref(null);
const url = new URL(window.location.href);
const showInfoWindow = ref(true);

const { importLibrary } = useMapsLoader();

onMounted(async () => {
	const [core, maps, marker] = await Promise.all([
		importLibrary('core'),
		importLibrary('maps'),
		importLibrary('marker')
	]);
	CoreLibrary = core;
	MapLibrary = maps;
	MarkerLibrary = marker;

	showInfoWindow.value = experienceType.value === 'prospect';

	const zoomParam = url.searchParams.get('zoom');
	zoom.value = zoomParam ? Number.parseInt(zoomParam, 10) : null;
	renderMap();
});

watch(
	() => props.matchingLocations,
	() => {
		// if the marker is in the matching locations, make it visible or hidden if not
		mapMarkers.forEach((marker) => {
			if (props.matchingLocations.some((match) => match.mmsClubId === marker.mmsClubId)) {
				marker.map = map;
			} else {
				marker.map = null;
			}
		});
	}
);

watch(
	() => props.mapCenter,
	() => {
		if (map) {
			map.setCenter(props.mapCenter);
			map.setZoom(zoom.value ?? defaultZoom);
			if (zoom.value) {
				// Clear zoom after initial load of the map
				zoom.value = null;
				url.searchParams.delete('zoom');
				window.history.replaceState(null, '', url);
			}
			renderMapCenterMarker(props.mapCenter.lat, props.mapCenter.lng);
		}
	}
);

watch(
	() => props.hoveredLocation,
	(newLocation, oldLocation) => {
		let oldHoveredMarker = null;
		let newHoveredMarker = null;

		if (oldLocation) {
			oldHoveredMarker = mapMarkers.find((marker) => marker.mmsClubId === oldLocation.mmsClubId);
		}
		if (newLocation) {
			newHoveredMarker = mapMarkers.find((marker) => marker.mmsClubId === newLocation.mmsClubId);
		}

		if (oldHoveredMarker) {
			if (oldHoveredMarker.mmsClubId !== props.selectedLocation?.mmsClubId) {
				showSmallMarker(oldHoveredMarker);
			}
		}
		if (newHoveredMarker) {
			showLargeMarker(newHoveredMarker);
		}
	}
);

watch(
	() => props.selectedLocation,
	(newSelectedLocation, oldSelectedLocation) => {
		if (!map) return;

		if (oldSelectedLocation) {
			const oldSelectedMarker = mapMarkers.find(
				(marker: { mmsClubId: number }) => marker.mmsClubId === oldSelectedLocation.mmsClubId
			);
			if (oldSelectedMarker) {
				showSmallMarker(oldSelectedMarker);
			}
		}

		if (newSelectedLocation) {
			map.panTo({
				lat: Number(newSelectedLocation.latitude),
				lng: Number(newSelectedLocation.longitude)
			});
			renderInfoWindow(newSelectedLocation);
		} else {
			infoWindow?.close();
		}
	}
);

const smallMarkerZIndex = 9999; // chosen arbitrarily

function showSmallMarker(marker: LtLocationMarker) {
	marker.content = smallLocationIcon();
	marker.zIndex = smallMarkerZIndex;

	// Events in advanced markers are attached to content so needs to be reattached
	marker.content?.addEventListener('mouseenter', () => {
		showLargeMarker(marker);
	});
}

function showLargeMarker(marker: LtLocationMarker) {
	marker.content = largeLocationIcon();
	// large markers indicate selected or hovered, so they show above small ones
	marker.zIndex = smallMarkerZIndex + 1;

	// Events in advanced markers are attached to content so needs to be reattached
	marker.content.addEventListener('mouseout', () => {
		if (marker.mmsClubId !== props.selectedLocation?.mmsClubId) {
			showSmallMarker(marker);
		}
	});
}

function renderMarker(location: Location, map: google.maps.Map) {
	const marker = new MarkerLibrary.AdvancedMarkerElement({
		position: new CoreLibrary.LatLng(Number(location.latitude), Number(location.longitude)),
		map,
		title: location.clubTitle,
		content: smallLocationIcon(),
		zIndex: smallMarkerZIndex
	});

	// Add the clubId to the marker so we can figure out which one it represents
	// Trick typescript into letting us do it.
	const ltMarker: LtLocationMarker = marker as LtLocationMarker;
	ltMarker.mmsClubId = location.mmsClubId;

	ltMarker.addListener('gmp-click', () => emit('marker-click', location));
	ltMarker.content?.addEventListener('mouseenter', () => {
		showLargeMarker(ltMarker);
	});

	mapMarkers.push(ltMarker);
}

function renderMapCenterMarker(lat: number, lng: number) {
	const position = new CoreLibrary.LatLng({ lat, lng });

	if (centerMarker) {
		// Formally remove marker to be sure listener is cleaned up by Google
		centerMarker.map = null;
		centerMarker = null;
	}

	centerMarker = new google.maps.marker.AdvancedMarkerElement({
		position,
		map,
		title: 'Location',
		content: mapCenterIcon()
	});
	centerMarker?.addListener('gmp-click', () => {
		map?.panTo(position);
	});
}

const emitMapBoundsSet = () => emit('map-bounds-set', map?.getBounds());

function renderMap() {
	map = new MapLibrary.Map(mapDiv.value as HTMLElement, mapConfig(CoreLibrary));

	infoWindow = new MapLibrary.InfoWindow({
		pixelOffset: new CoreLibrary.Size(0, 28)
	});

	props.allLocations.forEach((location: Location) => {
		renderMarker(location, map);
	});

	map.setCenter(props.mapCenter);

	const isDefaultCenter =
		props.mapCenter.lat === defaultCenter.lat && props.mapCenter.lng === defaultCenter.lng;

	// Draw a map center marker ONLY if it's not the default center
	if (!isDefaultCenter) {
		renderMapCenterMarker(props.mapCenter.lat, props.mapCenter.lng);
	}

	// if user clicks on map outside an info window, close the info window
	map.addListener('click', () => {
		infoWindow?.close();
		emit('info-window-close');
	});

	// if user clicks the close icon on the infoWindow
	infoWindow.addListener('closeclick', () => {
		// google closes the infoWindow in this case, so we don't need to
		emit('info-window-close');
	});

	// One-time initialize the map bounds stored in the parent component.
	// This allows us to compute initial matchingLocations.
	CoreLibrary.event.addListenerOnce(map, 'bounds_changed', emitMapBoundsSet);

	// But after that, only change these bounds when the user drags the map or
	// zooms. Earlier we were listening to `bounds_changed` here instead of
	// `dragend` and `zoom_changed`. That causes issues because when a user
	// clicks on a marker, our code centers the map on that marker, which
	// changes the map bounds and fires `bounds_changed`. If we record these new
	// bounds in the parent, new matchingLocations may be added/removed from the
	// list in the UI. While this is one way to implement things, it feels
	// jarring to click on a map marker and suddenly have the number of
	// matchingLocations change. On the other hand, it makes sense when these
	// change if you drag or zoom the map.
	map.addListener('zoom_changed', emitMapBoundsSet);
	map.addListener('dragstart', () => {
		// `dragstart` indicates the start of a user's drag of the map
		// `dragend` fires immediately when they let go of their drag,
		// but the map continues to pan as it slows smoothly to a stop.
		// `idle` fires when it stops, so this is when we want to calculate
		// the matching locations
		// `idle` is also fired after many other map changes that we don't want.
		// We only use it to indicate the REAL end of a drag.
		const handle = map?.addListener('idle', () => {
			emitMapBoundsSet();
			CoreLibrary.event.removeListener(handle);
		});
	});
}

function renderInfoWindow(location: Location) {
	if (!infoWindowEl.value) return;

	const marker = mapMarkers.find((marker) => marker.mmsClubId === location.mmsClubId);
	if (!marker) return;

	showLargeMarker(marker);

	infoWindow?.close();

	infoWindow?.setHeaderDisabled(true);

	infoWindow?.setContent(infoWindowEl.value);

	const minWidth = 992; // the minimum width at which we want to show the info window
	if (window.innerWidth < minWidth) return;

	infoWindow?.open(map, marker);
}

function closeInfoWindow() {
	infoWindow?.close();
	emit('info-window-close');
}

defineExpose({
	zoom
});
</script>

<style>
.location-map-canvas {
	position: absolute;
	top: 0;
	bottom: 0;
	left: 0;
	right: 0;
}
</style>
